import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import type { GastoDeServicio, ResumenDeGastos, ServicioPagado } from '@manejo-alcanza/contracts';
import { dolares, registrarFacturaGasto, resumenDeGastos } from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Tabla, Tile } from '../componentes/basicos';

/**
 * Lo que cuesta tener el sistema prendido (E8-02). Solo administrador y
 * desarrollador.
 *
 * Tres cosas que la pantalla no puede dejar de decir:
 *
 * - **De dónde sale cada cifra**: la API del proveedor (día a día, la recoge
 *   el recolector cada mañana) o la factura que alguien digitó. Cuando están
 *   las dos, la factura manda.
 * - **Cuándo una cifra no está**: «sin cifra», y el total con «≥». Nunca un
 *   US$0,00 que parezca que no se gastó nada.
 * - **Cuándo el recolector no corrió o falló**: una cifra vieja enseñada como
 *   la de hoy es peor que ninguna.
 */

const NOMBRE: Record<ServicioPagado, string> = {
  ANTHROPIC: 'Anthropic (Claude)',
  NEON: 'Neon (base de datos)',
  FLY: 'Fly.io (servidores)',
  VOYAGE: 'Voyage (embeddings)',
};

/** Más de esto sin una recolección y se avisa: el recolector corre cada 24 h. */
const HORAS_SIN_RECOLECTAR = 36;

export function Gastos(): ReactNode {
  const cliente = useQueryClient();
  const resumen = useQuery({ queryKey: ['gastos'], queryFn: () => resumenDeGastos(6) });

  if (resumen.isLoading) return <Cargando que="los gastos" />;
  if (resumen.isError || !resumen.data) return <Error_>No se pudieron cargar los gastos</Error_>;

  const r = resumen.data;
  const mesActual = r.hoy.slice(0, 7);
  const totalMes = r.totalPorMes.find((t) => t.mes === mesActual);
  const anthropic = r.servicios.find((s) => s.servicio === 'ANTHROPIC');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Lo que cuesta el sistema</h1>
        <p className="text-sm text-tenue">
          Neon, Fly.io, Anthropic y Voyage. Lo de la API se recoge cada mañana; las facturas se digitan aquí.
        </p>
      </div>

      <EstadoDeLaRecoleccion servicios={r.servicios} cargadoEn={resumen.dataUpdatedAt} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Tile
          etiqueta={`Este mes (${mesActual})`}
          valor={totalMes ? `${totalMes.sinCifra.length > 0 ? '≥ ' : ''}${dolares(totalMes.total)}` : 'sin cifra'}
          pie={
            totalMes && totalMes.sinCifra.length > 0
              ? `Sin cifra todavía: ${totalMes.sinCifra.map((s) => NOMBRE[s]).join(', ')}`
              : 'Con todos los proveedores'
          }
        />
        <Tile
          etiqueta="Anthropic, últimos 30 días"
          valor={dolares(anthropic?.ultimos30Dias ?? null)}
          pie="Lo que reporta su API, día por día"
        />
        <Tile etiqueta="Presupuesto objetivo" valor="< US$10 al mes" pie="CONTEXTO §14, de antes de Patroclo" />
      </div>

      <TablaDeMeses resumen={r} />
      <ConsumoNeon servicio={r.servicios.find((s) => s.servicio === 'NEON')} />

      <Card titulo="Digitar una factura">
        <FormularioFactura
          mesActual={mesActual}
          alGuardar={() => void cliente.invalidateQueries({ queryKey: ['gastos'] })}
        />
      </Card>
    </div>
  );
}

/** Lo primero que se ve si algo no corrió: no se enseña una cifra vieja como de hoy. */
function EstadoDeLaRecoleccion({
  servicios,
  cargadoEn,
}: {
  servicios: GastoDeServicio[];
  /**
   * Cuándo se trajeron los datos (`dataUpdatedAt` de react-query). Es contra eso
   * que se mide la antigüedad, y no contra `Date.now()` en el render, que no es
   * puro: el mismo componente diría una cosa distinta en cada repintado.
   */
  cargadoEn: number;
}): ReactNode {
  const avisos: ReactNode[] = [];
  for (const s of servicios.filter((x) => x.tieneApi)) {
    const u = s.ultimaRecoleccion;
    if (!u) {
      avisos.push(
        <Aviso key={s.servicio}>
          {NOMBRE[s.servicio]}: el recolector diario no ha corrido nunca. Se enciende con la variable{' '}
          <code>GASTOS_ACTIVO</code> del repositorio (docs/DESPLIEGUE.md).
        </Aviso>,
      );
      continue;
    }
    const horas = (cargadoEn - Date.parse(u.ocurrioEn)) / 3_600_000;
    if (u.resultado === 'ERROR') {
      avisos.push(
        <Error_ key={s.servicio}>
          {NOMBRE[s.servicio]}: la última recolección falló. {u.mensaje}
        </Error_>,
      );
    } else if (u.resultado === 'SIN_CONFIGURAR') {
      avisos.push(
        <Aviso key={s.servicio}>
          {NOMBRE[s.servicio]}: no se recoge. {u.mensaje}
        </Aviso>,
      );
    } else if (horas > HORAS_SIN_RECOLECTAR) {
      avisos.push(
        <Aviso key={s.servicio}>
          {NOMBRE[s.servicio]}: la última recolección fue hace {Math.round(horas / 24)} días. Las cifras de la API
          pueden estar viejas.
        </Aviso>,
      );
    }
  }
  return avisos.length > 0 ? <div className="space-y-2">{avisos}</div> : null;
}

function TablaDeMeses({ resumen }: { resumen: ResumenDeGastos }): ReactNode {
  const mesActual = resumen.hoy.slice(0, 7);
  return (
    <div className="space-y-1">
      <Tabla>
        <thead>
          <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
            <th className="px-3 py-2 font-medium">Proveedor</th>
            {resumen.meses.map((m) => (
              <th key={m} className="px-3 py-2 text-right font-medium">
                {m}
                {m === mesActual && <span className="block normal-case">a la fecha</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {resumen.servicios.map((s) => (
            <tr key={s.servicio} className="border-b border-borde">
              <td className="px-3 py-2">
                {NOMBRE[s.servicio]}
                {!s.tieneApi && <span className="block text-xs text-tenue">sin API: por factura</span>}
              </td>
              {s.porMes.map((m) => (
                <td key={m.mes} className="cifra px-3 py-2" title={m.notaFactura ?? undefined}>
                  <CeldaDeMes factura={m.factura} api={m.api} />
                </td>
              ))}
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="px-3 py-2">Total</td>
            {resumen.totalPorMes.map((t) => (
              <td
                key={t.mes}
                className="cifra px-3 py-2"
                title={t.sinCifra.length > 0 ? `Sin cifra: ${t.sinCifra.join(', ')}` : undefined}
              >
                {t.sinCifra.length === resumen.servicios.length ? (
                  <span className="text-tenue">sin cifra</span>
                ) : (
                  `${t.sinCifra.length > 0 ? '≥ ' : ''}${dolares(t.total)}`
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </Tabla>
      <p className="text-xs text-tenue">
        «factura» manda sobre «API» cuando un mes tiene las dos. La API cuenta días UTC (de 7 p. m. a 7 p. m. en
        Colombia), así que en los bordes del mes puede no cuadrar al centavo con la factura.
      </p>
    </div>
  );
}

function CeldaDeMes({ factura, api }: { factura: string | null; api: string | null }): ReactNode {
  if (factura !== null) {
    return (
      <>
        {dolares(factura)}
        <span className="block text-xs text-tenue">
          factura{api !== null && ` · API ${dolares(api)}`}
        </span>
      </>
    );
  }
  if (api !== null) {
    return (
      <>
        {dolares(api)}
        <span className="block text-xs text-tenue">API</span>
      </>
    );
  }
  return <span className="text-tenue">sin cifra</span>;
}

/**
 * Neon solo reporta uso, no plata: se enseña el uso, con su unidad. Los
 * CU-segundos pasan a CU-horas (÷ 3600, exacto); los bytes a GB (÷ 10⁹). El
 * almacenamiento viene en byte-horas y no se convierte: su paso a GB-mes no
 * está en la documentación de Neon, y una cifra inventada no se enseña.
 */
function ConsumoNeon({ servicio }: { servicio: GastoDeServicio | undefined }): ReactNode {
  if (!servicio || servicio.consumo.length === 0) return null;
  const meses = [...new Set(servicio.consumo.map((c) => c.mes))];
  const valor = (mes: string, metrica: string) =>
    servicio.consumo.find((c) => c.mes === mes && c.metrica === metrica)?.valor ?? null;
  const numero = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 });

  return (
    <Card titulo="Uso de Neon (lo que reporta su API, sin plata)">
      <Tabla>
        <thead>
          <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
            <th className="px-3 py-2 font-medium">Mes</th>
            <th className="px-3 py-2 text-right font-medium">Cómputo (CU-horas)</th>
            <th className="px-3 py-2 text-right font-medium">Transferencia pública (GB)</th>
          </tr>
        </thead>
        <tbody>
          {meses.map((m) => {
            const cu = valor(m, 'compute_unit_seconds');
            const bytes = valor(m, 'public_network_transfer_bytes');
            return (
              <tr key={m} className="border-b border-borde last:border-0">
                <td className="px-3 py-2">{m}</td>
                <td className="cifra px-3 py-2">{cu === null ? '0' : numero.format(Number(cu) / 3600)}</td>
                <td className="cifra px-3 py-2">{bytes === null ? '0' : numero.format(Number(bytes) / 1e9)}</td>
              </tr>
            );
          })}
        </tbody>
      </Tabla>
      <p className="mt-1 text-xs text-tenue">
        Neon omite las métricas en cero, así que una celda en 0 sí es cero. La plata de Neon sale de su factura.
      </p>
    </Card>
  );
}

function FormularioFactura({ mesActual, alGuardar }: { mesActual: string; alGuardar: () => void }): ReactNode {
  const [servicio, setServicio] = useState<ServicioPagado>('FLY');
  const [mes, setMes] = useState(mesActual);
  const [monto, setMonto] = useState('');
  const [nota, setNota] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: () =>
      registrarFacturaGasto({ servicio, mes, montoUsd: monto.replace(',', '.'), nota: nota.trim() || null }),
    onSuccess: () => {
      setError(null);
      setHecho(`Guardada: ${NOMBRE[servicio]}, ${mes}.`);
      setMonto('');
      setNota('');
      alGuardar();
    },
    onError: (e: Error) => {
      setHecho(null);
      setError(e.message);
    },
  });

  const enviar = (ev: FormEvent): void => {
    ev.preventDefault();
    guardar.mutate();
  };

  const campo = 'min-h-[44px] w-full rounded-md border border-borde bg-superficie px-2 py-1 text-base sm:min-h-0 sm:text-sm';

  return (
    <form onSubmit={enviar} className="grid gap-3 sm:grid-cols-5 sm:items-end">
      <label className="text-xs text-tenue">
        Proveedor
        <select value={servicio} onChange={(e) => setServicio(e.target.value as ServicioPagado)} className={campo}>
          {(Object.keys(NOMBRE) as ServicioPagado[]).map((s) => (
            <option key={s} value={s}>
              {NOMBRE[s]}
            </option>
          ))}
        </select>
      </label>
      <label className="text-xs text-tenue">
        Mes
        <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} required className={campo} />
      </label>
      <label className="text-xs text-tenue">
        Total de la factura (US$)
        <input
          inputMode="decimal"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          placeholder="4.87"
          required
          className={campo}
        />
      </label>
      <label className="text-xs text-tenue">
        Nota (opcional)
        <input value={nota} onChange={(e) => setNota(e.target.value)} maxLength={300} className={campo} />
      </label>
      <Boton submit deshabilitado={guardar.isPending || monto.trim() === ''}>
        {guardar.isPending ? 'Guardando…' : 'Guardar'}
      </Boton>
      {error !== null && <p className="text-sm text-alerta sm:col-span-5">{error}</p>}
      {hecho !== null && (
        <p className="text-sm text-tenue sm:col-span-5">
          {hecho} Si ese mes ya tenía factura, quedó corregida y la anterior en la auditoría.
        </p>
      )}
    </form>
  );
}
